import type {List} from 'immutable';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1, SubjectModelV2, TeacherModelV1} from '@albert-io/models';
import {getTeacherQuery} from 'client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react';
import {getActiveClassroomsWithSubjectsQuery} from 'client/generic/SubjectMenuRenderer/helpers/queries';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

// The teacher's classrooms with their subjects — the source for the aggregated class-subject grid.
// Mirrors the legacy SubjectLibrary/ClassSubjects query.
const subjectsInClassroomsQuery = (teacherId: string) =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .pageSize(250);

export interface ClassworkSubjects {
  isReady: boolean;
  /** A read failed — show the retry panel instead of the spinner/empty state. */
  hasError: boolean;
  /** Re-dirty the failed reads and refetch. */
  reload: () => void;
  /** Settled-and-fresh empty: false while a revisit refetch is in flight, so a stale cached-empty
   *  list can't flash the empty state before the refreshed subjects arrive. */
  hasNoSubjects: boolean;
  /** Distinct subjects across all classrooms, sorted by name, filtered by the Show-hidden toggle. */
  subjects: SubjectModelV2[];
  /** A hidden subject exists in the aggregate — gates the Filters control. */
  hasHiddenSubjects: boolean;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
  /** Feeds SubjectCourseCard's Practice Settings dropdown (per-class open/close toggles). */
  activeClassrooms: List<ClassroomModelV1> | undefined;
  /** Licensed teacher at a licensed school — gates the per-card Practice Settings dropdown. */
  isUserLicensedTeacher: boolean;
}

export const useClassworkSubjects = (): ClassworkSubjects => {
  // Bridged: the query below is built during render, so the masquerade read that keys its endpoint
  // must be subscription-based — a bare render read is invisible to the compiled hook's memoization.
  const teacherId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');
  const classroomsQuery = useMemo(() => subjectsInClassroomsQuery(teacherId), [teacherId]);
  // These two build their endpoints from masqueradeStore inside cross-area helpers
  // (client/Classrooms/.../TeacherSubjectsList.react, client/generic/SubjectMenuRenderer/helpers/queries),
  // so bridging them belongs to those areas' batches, not this one.
  const activeClassroomsQuery = useMemo(() => getActiveClassroomsWithSubjectsQuery(), []);
  const teacherQuery = useMemo(() => getTeacherQuery(), []);
  const queries = useMemo(
    () => [classroomsQuery, activeClassroomsQuery, teacherQuery],
    [classroomsQuery, activeClassroomsQuery, teacherQuery]
  );
  // Refetch on revisit rather than serving a stale cache — subjects added from the library since
  // the last visit otherwise need a hard refresh to appear. Only populated caches are invalidated:
  // a cold mount's initial requests are already fresh, and dirtying them re-fetches both lists.
  useEffect(() => {
    [classroomsQuery, activeClassroomsQuery].forEach((query) => {
      if (query.isResourceReady()) {
        query.invalidateInterest();
      }
    });
  }, [classroomsQuery, activeClassroomsQuery]);
  const {isReady, resources} = useMandarkQueries(queries);

  // A read is invalid once its interest is in an error state. Mandark caches errors forever, so
  // this stays true until reload() re-dirties it. Bridged (re-runs on state-tree emits) so a
  // failed fetch surfaces and a retry re-reads — a bare render read would be frozen by the
  // React Compiler.
  const hasError = useFluxStore(sessionStore, () =>
    queries.some((query) => !query.isResourceValid())
  );
  // Bridged for the same reason: the revisit refetch settling must re-run this read.
  const isClassroomsQueryReady = useFluxStore(sessionStore, () =>
    classroomsQuery.isResourceReady()
  );
  const reload = useCallback(() => {
    // Re-dirty the errored interests so the next read re-fetches: invalidateInterest emits a store
    // change → the bridged reads re-run → the read hits the network again.
    queries.forEach((query) => query.invalidateInterest());
  }, [queries]);

  const classrooms = resources[0] as List<ClassroomModelV1> | undefined;
  const activeClassrooms = resources[1] as List<ClassroomModelV1> | undefined;
  const teacher = resources[2] as TeacherModelV1 | undefined;

  const [showHidden, setShowHidden] = useState(false);

  const allSubjects = useMemo<SubjectModelV2[]>(() => {
    if (!isReady || !classrooms) {
      return [];
    }
    const seen = new Set<string>();
    const aggregated: SubjectModelV2[] = [];
    classrooms.forEach((classroom) => {
      classroom.getSubjects().forEach((subject) => {
        if (!seen.has(subject.getId())) {
          seen.add(subject.getId());
          aggregated.push(subject);
        }
      });
    });
    return aggregated.sort((a, b) => a.getName().localeCompare(b.getName()));
  }, [isReady, classrooms]);

  const hasHiddenSubjects = allSubjects.some((subject) => subject.isHidden());
  const subjects = showHidden ? allSubjects : allSubjects.filter((subject) => !subject.isHidden());

  // The aggregate ("all classwork") view spans every classroom/school, so gate on whether the teacher
  // is licensed at ANY active-license school (is_licensed)
  const isUserLicensedTeacher = useMemo(() => {
    if (!isReady || !teacher || typeof teacher.getMeta !== 'function') {
      return false;
    }
    return Boolean(teacher.getMeta().isLicensed());
  }, [isReady, teacher]);

  return {
    isReady,
    hasError,
    reload,
    hasNoSubjects: isReady && allSubjects.length === 0 && isClassroomsQueryReady,
    subjects,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    activeClassrooms,
    isUserLicensedTeacher
  };
};
